import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Copy, KeyRound, RotateCcw, Trash2, Eye, History, Plus, ShieldAlert, Power, PowerOff } from "lucide-react";
import {
  listMyApiKeys,
  createMyApiKey,
  rotateMyApiKey,
  revokeMyApiKey,
  setMyApiKeyEnabled,
  listMyApiKeyEvents,
} from "@/lib/personal-api-keys.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/_account/api-keys")({
  head: () => ({
    meta: [
      { title: "API keys — CoderLity" },
      { name: "description", content: "Create, rotate, and revoke your personal API keys." },
    ],
  }),
  component: ApiKeysPage,
});

const SUGGESTED_SCOPES = ["read", "write", "admin", "billing:read", "orders:read", "orders:write"];

function ApiKeysPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listMyApiKeys);
  const createFn = useServerFn(createMyApiKey);
  const rotateFn = useServerFn(rotateMyApiKey);
  const revokeFn = useServerFn(revokeMyApiKey);
  const toggleFn = useServerFn(setMyApiKeyEnabled);

  const { data, isLoading } = useQuery({
    queryKey: ["my-api-keys"],
    queryFn: () => listFn({ data: {} as any }),
  });

  const keys = data?.keys ?? [];

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [scopeInput, setScopeInput] = useState("");
  const [expiresAt, setExpiresAt] = useState<string>(""); // YYYY-MM-DDTHH:mm
  const [rateLimit, setRateLimit] = useState<number>(60);
  const [plaintext, setPlaintext] = useState<string | null>(null);

  // Events dialog
  const [eventsKeyId, setEventsKeyId] = useState<string | null>(null);

  const resetCreate = () => {
    setName("");
    setScopes([]);
    setScopeInput("");
    setExpiresAt("");
    setRateLimit(60);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        name,
        scopes,
        rate_limit_per_minute: Number(rateLimit) || 60,
      };
      if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
      return createFn({ data: payload });
    },
    onSuccess: (res) => {
      setPlaintext(res.plaintext);
      resetCreate();
      qc.invalidateQueries({ queryKey: ["my-api-keys"] });
      toast.success("API key created");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to create key"),
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => rotateFn({ data: { id } }),
    onSuccess: (res) => {
      setPlaintext(res.plaintext);
      qc.invalidateQueries({ queryKey: ["my-api-keys"] });
      toast.success("Key rotated. Copy the new secret now.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to rotate"),
  });

  const revokeMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      revokeFn({ data: { id, ...(reason ? { reason } : {}) } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-api-keys"] });
      toast.success("Key revoked");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to revoke"),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      toggleFn({ data: { id, enabled } }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["my-api-keys"] });
      toast.success(vars.enabled ? "Key enabled" : "Key disabled");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update key"),
  });

  const addScope = (s: string) => {
    const v = s.trim();
    if (!v) return;
    if (!/^[a-zA-Z0-9:_.\-]+$/.test(v)) return toast.error("Invalid scope format");
    if (scopes.includes(v)) return;
    if (scopes.length >= 40) return toast.error("Max 40 scopes");
    setScopes([...scopes, v]);
    setScopeInput("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">API keys</h1>
          <p className="text-sm text-muted-foreground">
            Personal tokens for programmatic access. Treat them like passwords.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetCreate(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2 h-4 w-4" /> New key</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create API key</DialogTitle>
              <DialogDescription>You'll see the secret once. Copy and store it safely.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="My laptop, CI deploy, ..." maxLength={100} />
              </div>
              <div className="space-y-2">
                <Label>Scopes</Label>
                <div className="flex flex-wrap gap-1.5">
                  {scopes.map((s) => (
                    <Badge key={s} variant="secondary" className="gap-1">
                      {s}
                      <button
                        className="ml-1 text-muted-foreground hover:text-foreground"
                        onClick={() => setScopes(scopes.filter((x) => x !== s))}
                        aria-label={`Remove ${s}`}
                      >×</button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={scopeInput}
                    onChange={(e) => setScopeInput(e.target.value)}
                    placeholder="e.g. orders:read"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); addScope(scopeInput); }
                    }}
                  />
                  <Button type="button" variant="secondary" onClick={() => addScope(scopeInput)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {SUGGESTED_SCOPES.filter((s) => !scopes.includes(s)).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => addScope(s)}
                      className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-secondary"
                    >+ {s}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="expires">Expires at</Label>
                  <Input id="expires" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                  <p className="text-xs text-muted-foreground">Leave empty for no expiry.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate">Rate limit (req/min)</Label>
                  <Input id="rate" type="number" min={1} max={10000} value={rateLimit} onChange={(e) => setRateLimit(Number(e.target.value))} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                disabled={!name.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >{createMutation.isPending ? "Creating..." : "Create key"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Your keys</CardTitle>
          <CardDescription>{keys.length} {keys.length === 1 ? "key" : "keys"}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : keys.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No API keys yet. Create one to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Prefix</TableHead>
                    <TableHead>Scopes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map((k: any) => {
                    const expired = k.expires_at && new Date(k.expires_at) < new Date();
                    return (
                      <TableRow key={k.id}>
                        <TableCell className="font-medium">{k.name}</TableCell>
                        <TableCell><code className="text-xs">{k.prefix}</code></TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(k.scopes ?? []).length === 0 ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              k.scopes.map((s: string) => (
                                <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                              ))
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {k.status === "revoked" ? (
                            <Badge variant="destructive">Revoked</Badge>
                          ) : expired ? (
                            <Badge variant="secondary">Expired</Badge>
                          ) : k.status === "disabled" ? (
                            <Badge variant="secondary">Disabled</Badge>
                          ) : (
                            <Badge>Active</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {k.expires_at ? format(new Date(k.expires_at), "yyyy-MM-dd HH:mm") : "Never"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {k.last_used_at ? format(new Date(k.last_used_at), "yyyy-MM-dd HH:mm") : "Never"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            <Button variant="ghost" size="icon" title="Activity" onClick={() => setEventsKeyId(k.id)}>
                              <History className="h-4 w-4" />
                            </Button>
                            {k.status !== "revoked" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title={k.status === "disabled" ? "Enable" : "Disable"}
                                disabled={toggleMutation.isPending}
                                onClick={() =>
                                  toggleMutation.mutate({ id: k.id, enabled: k.status === "disabled" })
                                }
                              >
                                {k.status === "disabled" ? (
                                  <Power className="h-4 w-4 text-emerald-500" />
                                ) : (
                                  <PowerOff className="h-4 w-4 text-amber-500" />
                                )}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Rotate"
                              disabled={rotateMutation.isPending}
                              onClick={() => rotateMutation.mutate(k.id)}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                            {k.status !== "revoked" && (
                              <RevokeButton onConfirm={(reason) => revokeMutation.mutate({ id: k.id, reason })} />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plaintext reveal dialog */}
      <Dialog open={!!plaintext} onOpenChange={(o) => { if (!o) setPlaintext(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5" /> Copy your secret</DialogTitle>
            <DialogDescription className="flex items-center gap-2 text-amber-500">
              <ShieldAlert className="h-4 w-4" />
              This is the only time you'll see this secret.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <code className="block break-all rounded-md border border-border bg-muted/50 p-3 text-sm">
              {plaintext}
            </code>
            <Button
              className="w-full"
              onClick={() => {
                if (plaintext) {
                  navigator.clipboard.writeText(plaintext);
                  toast.success("Copied to clipboard");
                }
              }}
            >
              <Copy className="mr-2 h-4 w-4" /> Copy
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Events dialog */}
      <EventsDialog keyId={eventsKeyId} onClose={() => setEventsKeyId(null)} />
    </div>
  );
}

function RevokeButton({ onConfirm }: { onConfirm: (reason?: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Revoke">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
          <AlertDialogDescription>
            Requests using this key will start failing immediately. This cannot be undone (you can rotate to reissue).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reason">Reason (optional)</Label>
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="e.g. compromised, no longer needed"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(reason || undefined)}>Revoke</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EventsDialog({ keyId, onClose }: { keyId: string | null; onClose: () => void }) {
  const eventsFn = useServerFn(listMyApiKeyEvents);
  const { data, isLoading } = useQuery({
    queryKey: ["my-api-key-events", keyId],
    queryFn: () => eventsFn({ data: { api_key_id: keyId! } }),
    enabled: !!keyId,
  });

  const events = useMemo(() => data?.events ?? [], [data]);

  return (
    <Dialog open={!!keyId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Activity</DialogTitle>
          <DialogDescription>Recent events for this key.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No events yet.</p>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((e: any) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-xs">{format(new Date(e.created_at), "yyyy-MM-dd HH:mm:ss")}</TableCell>
                    <TableCell><Badge variant="outline">{e.action}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.ip ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {e.metadata && Object.keys(e.metadata).length > 0
                        ? JSON.stringify(e.metadata)
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
